// ConversationHistory - Chat conversation history sidebar
import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  TrashIcon,
  ChatBubbleLeftIcon,
  ChevronLeftIcon,
  ArrowPathIcon,
} from 'components/icons';
import { aiApi } from '../../services/api';
import toast from 'react-hot-toast';
import Drawer from '../ui/Drawer';
import EmptyState from '../ui/EmptyState';
import { SkeletonBlock } from '../ui/Skeleton';

export interface Conversation {
  id: string;
  title: string;
  opportunityId?: string;
  context?: {
    opportunity?: {
      title?: string;
      agency?: string;
    };
  };
  createdAt: string;
  updatedAt: string;
  _count?: {
    messages: number;
  };
}

interface ConversationHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (conversation: Conversation) => void;
  /** Fires after a conversation is successfully deleted. Parent can
   * inspect whether it matched the active conversation and reset chat. */
  onDeleted?: (deletedId: string) => void;
  currentConversationId?: string | null;
  opportunityId?: string;
}

export const ConversationHistory: React.FC<ConversationHistoryProps> = ({
  isOpen,
  onClose,
  onSelect,
  onDeleted,
  currentConversationId,
  opportunityId,
}) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchConversations();
    }
  }, [isOpen]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const response = await aiApi.getConversations();
      setConversations(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      toast.error('Failed to load conversation history');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    
    if (!window.confirm('Delete this conversation? This cannot be undone.')) {
      return;
    }

    setDeletingId(conversationId);
    try {
      await aiApi.deleteConversation(conversationId);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      toast.success('Conversation deleted');
      onDeleted?.(conversationId);
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      toast.error('Failed to delete conversation');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  // Filter conversations for this opportunity if opportunityId is provided
  const filteredConversations = opportunityId
    ? conversations.filter(c => c.opportunityId === opportunityId)
    : conversations;

  return (
    <>
      <Drawer
        open={isOpen}
        onClose={onClose}
        title="History"
        icon={ClockIcon}
        side="right"
        width="w-80"
        headerActions={
          <button
            onClick={fetchConversations}
            disabled={loading}
            aria-label="Refresh conversation history"
            className="p-1.5 rounded-token text-content-subtle hover:text-content hover:bg-surface disabled:opacity-50 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
          </button>
        }
        footer={
          <p className="px-4 py-2 bg-surface-sunk text-caption text-content-muted text-center">
            {conversations.length} conversation{conversations.length !== 1 ? 's' : ''} total
          </p>
        }
      >
        {loading && conversations.length === 0 ? (
          <div className="p-4 space-y-3">
            <span className="sr-only" role="status">Loading conversations</span>
            <SkeletonBlock className="h-14" />
            <SkeletonBlock className="h-14" />
            <SkeletonBlock className="h-14" />
          </div>
        ) : filteredConversations.length === 0 ? (
          <EmptyState
            icon={ChatBubbleLeftIcon}
            title="No conversations yet"
            description={
              opportunityId
                ? 'Nothing saved for this opportunity yet.'
                : 'Ask a question to start one.'
            }
          />
        ) : (
          <ul className="divide-y divide-border">
            {filteredConversations.map((conversation) => (
              /* Select and delete are siblings inside a row. Previously the
                 delete button was nested inside the select button — invalid
                 HTML that browsers resolve inconsistently and that leaves the
                 delete action unreachable by keyboard. */
              <li
                key={conversation.id}
                className={`group flex items-start gap-1 pr-2 transition-colors hover:bg-surface-sunk ${
                  currentConversationId === conversation.id
                    ? 'bg-accent-sunk border-l-2 border-l-accent'
                    : 'border-l-2 border-l-transparent'
                }`}
              >
                <button
                  onClick={() => onSelect(conversation)}
                  aria-current={currentConversationId === conversation.id ? 'true' : undefined}
                  className="flex-1 min-w-0 text-left px-4 py-3 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
                >
                  <p className="text-body font-semibold text-content truncate">
                    {conversation.title || 'Untitled Conversation'}
                  </p>
                  {conversation.context?.opportunity?.title && (
                    <p className="text-caption text-content-muted truncate mt-0.5">
                      {conversation.context.opportunity.title}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-caption text-content-subtle">
                      {formatDate(conversation.updatedAt)}
                    </span>
                    {conversation._count?.messages && (
                      <span className="text-caption text-content-subtle">
                        • {conversation._count.messages} messages
                      </span>
                    )}
                  </div>
                </button>
                <button
                  onClick={(e) => handleDelete(e, conversation.id)}
                  disabled={deletingId === conversation.id}
                  aria-label={`Delete conversation "${conversation.title || 'Untitled'}"`}
                  className="mt-3 p-1 rounded text-content-subtle hover:text-danger hover:bg-danger-sunk opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-danger"
                >
                  {deletingId === conversation.id ? (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <TrashIcon className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Drawer>

      {/* Toggle handle, shown when the drawer is closed */}
      {!isOpen && (
        <button
          onClick={() => onClose()}
          aria-label="Show conversation history"
          className="fixed right-0 top-1/2 -translate-y-1/2 bg-surface-raised border border-border border-r-0 rounded-l-token p-2 shadow-e2 z-30 hover:bg-surface-sunk lg:hidden"
        >
          <ChevronLeftIcon className="h-5 w-5 text-content-muted" aria-hidden="true" />
        </button>
      )}
    </>
  );
};

export default ConversationHistory;
